import { useEffect, useRef, useState } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { promptNotifications } from "@/lib/onesignal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  User, ShieldCheck, Bell, SlidersHorizontal, ArrowRight, LogOut, Crown, Users,
  Palette, Check, Sparkles, LifeBuoy, Send, Loader2, Plus, Bot, MessageSquare,
  CandlestickChart, ImagePlus, ChevronDown, Lock, Headphones, Gift,
  KeyRound, ExternalLink, Trash2, Wallet,
} from "lucide-react";
import { useTheme, ACCENT_COLORS } from "@/context/ThemeContext";
import { BotAvatarUpload } from "@/components/BotAvatarUpload";
import { ReferralCard } from "@/components/ReferralCard";

const TICKET_CATEGORIES = [
  "Peman / Payment",
  "Koneksyon MT5 / MT5 Connection",
  "Bòt la pa mache / Bot not working",
  "Abònman / Subscription",
  "Kont / Account access",
  "Enstalasyon / Setup",
  "Lòt / Other",
];

function Section({ icon: Icon, title, children, action, id, openId, onToggle }) {
  const open = openId === id;
  return (
    <div className="border border-[#14233d] bg-[#0A1120] rounded-3xl overflow-hidden">
      <div className="w-full flex items-center justify-between gap-2 px-5 py-4 hover:bg-[#0d1728] transition-colors">
        <button type="button" onClick={() => onToggle(open ? null : id)} data-testid={`section-${id}`}
          className="flex items-center gap-2 text-left flex-1 min-w-0">
          <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--sfx-accent)" }} />
          <span className="font-heading font-semibold truncate">{title}</span>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {action}
          <button type="button" onClick={() => onToggle(open ? null : id)} aria-label="Toggle section"
            data-testid={`section-toggle-${id}`}>
            <ChevronDown className={`h-4 w-4 text-[#5f7ba6] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && <div className="p-5 pt-4 space-y-4 border-t border-[#14233d] fade-up">{children}</div>}
    </div>
  );
}

/* ------------------------------- Assistant ------------------------------- */
function AssistantDialog({ onRead }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [agentMode, setAgentMode] = useState(() => localStorage.getItem("sfx_agent_mode") === "1");
  const scrollRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem("sfx_asst_session") ||
    (() => { const s = `sess-${Date.now()}`; localStorage.setItem("sfx_asst_session", s); return s; })()
  ).current;

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/assistant/history?session_id=${sessionId}`);
      setMessages(data.map((m) => ({ role: m.role, content: m.content, agent_name: m.agent_name })));
      if (data.some((m) => m.role === "agent")) { setAgentMode(true); localStorage.setItem("sfx_agent_mode", "1"); }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    loadHistory();
    // Opening the chat marks any agent reply as seen (clears the Settings unread dot).
    api.post("/assistant/mark-read", { session_id: sessionId }).then(() => onRead?.()).catch(() => {});
    // Poll so a live agent's replies appear in the client's chat without a refresh.
    const t = setInterval(loadHistory, 6000);
    return () => clearInterval(t);
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || busy) return;
    setText("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      if (agentMode) {
        await api.post("/assistant/contact-agent", { session_id: sessionId, message: msg });
      } else {
        const { data } = await api.post("/assistant/chat", { session_id: sessionId, message: msg });
        setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const [connecting, setConnecting] = useState(false);
  const contactAgent = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      // A fixed escalation note — do NOT re-post the client's last AI question (that would
      // duplicate it in the thread). The agent sees the full history in the support inbox.
      const msg = "Bonjou, mwen ta renmen pale ak yon ajan reyèl.";
      await api.post("/assistant/contact-agent", { session_id: sessionId, message: msg });
      setAgentMode(true);
      localStorage.setItem("sfx_agent_mode", "1");
      setMessages((m) => [...m, { role: "assistant",
        content: "✅ Ou konekte ak sipò. Kounye a ekri dirèkteman la a — yon ajan reyèl ap reponn ou nan chat sa a menm." }]);
      toast.success("Konekte ak yon ajan sipò");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConnecting(false);
    }
  };

  const suggestions = [
    "Ki jan pou m konekte kont MT5 mwen?",
    "Esplike m volatilite nan mache a",
    "Kijan Smart Scalper Fx la fonksyone?",
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-assistant-button"
          className="w-full h-11 rounded-2xl text-white font-semibold hover:opacity-90"
          style={{ backgroundColor: "var(--sfx-accent)" }}>
          <Sparkles className="mr-2 h-4 w-4" /> Ouvri Assistant
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="assistant-fullscreen"
        className="bg-[#060c18] text-white border-0 p-0 gap-0 max-w-full w-screen h-[100dvh] left-0 top-0 translate-x-0 translate-y-0 rounded-none sm:rounded-none flex flex-col overflow-hidden">
        <DialogHeader className="px-5 py-4 border-b border-[#14233d] shrink-0">
          <DialogTitle className="font-heading flex items-center gap-2 pr-8">
            <span className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: "var(--sfx-accent)" }}>
              <Bot className="h-4 w-4 text-white" />
            </span>
            Smart Scalper Fx Assistant
          </DialogTitle>
          <DialogDescription className="sr-only">Chat asistan Smart FX — poze kesyon oswa pale ak yon ajan reyèl.</DialogDescription>
        </DialogHeader>

        {/* Talk-to-a-human bar (kept clear of the top-right close button) */}
        <button data-testid="contact-agent-button" onClick={contactAgent} disabled={connecting}
          className="mx-4 mt-3 mb-1 h-11 rounded-xl bg-[#00F593]/10 border border-[#00F593]/40 text-[#00F593] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#00F593]/20 transition-colors disabled:opacity-60">
          {connecting ? <><Loader2 className="h-4 w-4 animate-spin" /> Ap konekte...</> : <><Headphones className="h-4 w-4" /> Pale ak yon ajan reyèl</>}
        </button>

        <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {messages.length === 0 && !busy && (
            <div className="space-y-3">
              <p className="text-sm text-[#7f9bc4]">Mande m nenpòt bagay sou mache a, volatilite, plan trading, oswa kijan app la mache.</p>
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <button key={s} data-testid="assistant-suggestion" onClick={() => setText(s)}
                    className="w-full text-left text-xs px-3 py-2 rounded-xl border border-[#14233d] bg-[#060c18] text-[#a9c4ec] hover:border-[#1C8CFF]/50 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} data-testid={`assistant-msg-${m.role}`}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#14233d] text-white rounded-br-sm"
                  : m.role === "agent" ? "bg-[#00F593]/15 border border-[#00F593]/40 text-[#c6ffe7] rounded-bl-sm"
                  : "bg-[#060c18] border border-[#14233d] text-[#dce8fb] rounded-bl-sm"}`}>
                {m.role === "agent" && (
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[#00F593] mb-1 flex items-center gap-1">
                    <Headphones className="h-3 w-3" /> {m.agent_name || "Ajan Sipò"}
                  </div>
                )}
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="px-3.5 py-2.5 rounded-2xl bg-[#060c18] border border-[#14233d] text-[#7f9bc4] text-sm flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Assistant lan ap reflechi…
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-t border-[#14233d] bg-[#060c18]">
          <Input data-testid="assistant-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
            placeholder="Tape mesaj ou..."
            className="bg-[#0A1120] border-[#14233d] rounded-full h-11 focus-visible:ring-0" />
          <Button data-testid="assistant-send-button" onClick={send} disabled={busy || !text.trim()}
            className="h-11 w-11 shrink-0 rounded-full p-0 text-white" style={{ backgroundColor: "var(--sfx-accent)" }}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------- Support tickets ------------------------------- */
function SupportSection({ openId, onToggle }) {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tickets/me").then(({ data }) => setTickets(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!category || !message.trim()) { toast.error("Chwazi yon pwoblèm epi dekri li."); return; }
    setBusy(true);
    try {
      await api.post("/tickets", { category, message: message.trim() });
      toast.success("Ticket ou voye bay CEO a");
      setCategory(""); setMessage(""); setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section icon={LifeBuoy} title="Support & Ticket" id="support" openId={openId} onToggle={onToggle} action={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" data-testid="new-ticket-button"
            className="h-8 rounded-full px-3 text-white font-semibold hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Nouvo
          </Button>
        </DialogTrigger>
        <DialogContent data-testid="ticket-fullscreen"
          className="bg-[#060c18] text-white border-0 p-0 gap-0 max-w-full w-screen h-[100dvh] left-0 top-0 translate-x-0 translate-y-0 rounded-none sm:rounded-none flex flex-col overflow-hidden">
          <DialogHeader className="px-5 py-4 border-b border-[#14233d] shrink-0">
            <DialogTitle className="font-heading flex items-center gap-2 pr-8"><LifeBuoy className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} />Voye yon Ticket bay CEO a</DialogTitle>
            <DialogDescription className="sr-only">Fòm pou voye yon ticket sipò bay CEO a.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            <div>
              <Label className="text-xs text-[#7f9bc4]">Ki pwoblèm ou genyen?</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="ticket-category-select" className="mt-1.5 h-11 rounded-xl bg-[#0A1120] border-[#14233d]">
                  <SelectValue placeholder="Chwazi yon pwoblèm" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A1120] border-[#14233d] text-white">
                  {TICKET_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#7f9bc4]">Dekri sa ou bezwen an</Label>
              <Textarea data-testid="ticket-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Eksplike pwoblèm ou an oswa sa ou bezwen..."
                className="mt-1.5 min-h-[160px] bg-[#0A1120] border-[#14233d] rounded-xl focus-visible:ring-0" />
            </div>
          </div>
          <div className="px-5 py-4 border-t border-[#14233d] shrink-0">
            <Button data-testid="ticket-submit-button" onClick={submit} disabled={busy}
              className="w-full h-12 rounded-full text-white font-semibold" style={{ backgroundColor: "var(--sfx-accent)" }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Voye Ticket</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    }>
      <p className="text-xs text-[#5f7ba6] -mt-1">Rankontre yon pwoblèm? Voye yon ticket, CEO a ap reponn ou dirèkteman.</p>
      {tickets.length === 0 ? (
        <div className="text-sm text-[#5f7ba6] py-2">Ou poko gen okenn ticket.</div>
      ) : (
        <div className="space-y-3" data-testid="my-tickets-list">
          {tickets.map((t) => (
            <div key={t.id} data-testid={`my-ticket-${t.id}`} className="rounded-2xl border border-[#14233d] bg-[#060c18] p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-heading font-semibold text-sm">{t.category}</span>
                <span className={`font-mono-term text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  t.status === "resolved" ? "bg-[#22e07b]/15 text-[#22e07b]" : "bg-[#f5a623]/15 text-[#f5a623]"}`}>
                  {t.status === "resolved" ? "Rezoud" : "Ouvè"}
                </span>
              </div>
              <p className="text-sm text-[#a9c4ec] mt-1.5 whitespace-pre-wrap">{t.message}</p>
              {t.reply && (
                <div className="mt-3 pt-3 border-t border-[#14233d] flex gap-2">
                  <MessageSquare className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--sfx-accent)" }} />
                  <div>
                    <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Repons CEO</div>
                    <p className="text-sm text-[#dce8fb] mt-0.5 whitespace-pre-wrap">{t.reply}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ------------------------------- Client MetaAPI token (bring-your-own) ------------------------------- */
function MetaApiSection({ openId, onToggle }) {
  const [status, setStatus] = useState({ connected: false, masked: "" });
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const load = () => api.get("/account/metaapi-token").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const t = token.trim();
    if (t.length < 20) { toast.error("Token an twò kout. Kole token MetaAPI konplè a."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/account/metaapi-token", { token: t });
      setStatus({ connected: true, masked: data.masked, verified: data.verified });
      setToken("");
      if (data.verified) toast.success("Token MetaAPI verifye e valab! Kont ou yo ap kouri anba pwòp kle pa w.");
      else toast.warning(data.note || "Token anrejistre, men nou pa t ka verifye l kounye a.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Retire token MetaAPI ou? App la ap retounen sou kle default platfòm nan.")) return;
    setBusy(true);
    try {
      await api.delete("/account/metaapi-token");
      setStatus({ connected: false, masked: "" });
      toast.success("Token retire. App la ap itilize kle default platfòm nan.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const [testing, setTesting] = useState(false);
  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/account/metaapi-token/test");
      setStatus((s) => ({ ...s, verified: data.verified }));
      if (data.verified) toast.success("Koneksyon MetaAPI valab ✓");
      else toast.error("Token MetaAPI a pa valab ankò. Jenere yon nouvo epi mete l.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <Section icon={KeyRound} title="MetaAPI pa w (opsyonèl)" id="metaapi" openId={openId} onToggle={onToggle}
      action={status.connected && (
        <span data-testid="metaapi-connected-badge" className="text-[10px] font-mono-term uppercase tracking-widest text-[#22e07b] bg-[#22e07b]/10 px-2 py-0.5 rounded-full">Konekte</span>
      )}>
      <p className="text-xs text-[#5f7ba6] -mt-1">
        Konekte pwòp token MetaAPI pa w pou kont ou yo kouri anba abònman pa w. Si w pa mete youn, app la ap itilize kle default platfòm nan.
      </p>

      {status.connected ? (
        <div data-testid="metaapi-connected-panel" className="border border-[#22e07b]/30 bg-[#22e07b]/5 rounded-2xl p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="h-4 w-4 text-[#22e07b] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                Token pa w konekte
                {status.verified
                  ? <span data-testid="metaapi-verified-tag" className="text-[9px] font-mono-term uppercase tracking-widest text-[#22e07b] bg-[#22e07b]/10 px-1.5 py-0.5 rounded-full">Verifye ✓</span>
                  : <span data-testid="metaapi-unverified-tag" className="text-[9px] font-mono-term uppercase tracking-widest text-[#f5a623] bg-[#f5a623]/10 px-1.5 py-0.5 rounded-full">Pa verifye</span>}
              </div>
              <div className="font-mono-term text-xs text-[#8fb4e6] truncate">{status.masked}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={testConnection} disabled={testing} data-testid="metaapi-test-button" variant="outline"
              className="h-9 rounded-full border-[#00F593]/40 bg-transparent text-[#00F593] hover:bg-[#00F593]/10">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1.5" /> Teste</>}
            </Button>
            <Button onClick={remove} disabled={busy} data-testid="metaapi-remove-button" variant="outline"
              className="h-9 rounded-full border-[#ff3b30]/40 bg-transparent text-[#ff5b5b] hover:bg-[#ff3b30]/10">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1.5" /> Retire</>}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label className="text-xs text-[#888]">Token MetaAPI ou</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input data-testid="metaapi-token-input" value={token} onChange={(e) => setToken(e.target.value)}
              placeholder="Kole token MetaAPI ou isit la..." type="password"
              className="bg-[#060c18] border-[#14233d] rounded-xl focus-visible:ring-0" />
            <Button onClick={save} disabled={busy} data-testid="metaapi-save-button"
              className="h-10 rounded-xl text-white font-semibold shrink-0 px-6" style={{ backgroundColor: "var(--sfx-accent)" }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anrejistre"}
            </Button>
          </div>
        </div>
      )}

      {/* Recharge button */}
      <a href="https://app.metaapi.cloud/billing" target="_blank" rel="noopener noreferrer"
        data-testid="metaapi-recharge-button"
        className="flex items-center justify-center gap-2 w-full h-11 rounded-2xl bg-[#00F593]/10 border border-[#00F593]/40 text-[#00F593] text-sm font-semibold hover:bg-[#00F593]/20 transition-colors">
        <Wallet className="h-4 w-4" /> Rechaje kont MetaAPI ou <ExternalLink className="h-3.5 w-3.5" />
      </a>

      {/* Step-by-step guide */}
      <div className="border border-[#14233d] bg-[#060c18] rounded-2xl overflow-hidden">
        <button onClick={() => setShowGuide((s) => !s)} data-testid="metaapi-guide-toggle"
          className="w-full flex items-center justify-between px-4 py-3 text-left">
          <span className="flex items-center gap-2 text-sm font-semibold text-[#8fb4e6]">
            <LifeBuoy className="h-4 w-4" /> Gid konplè A → Z: Kijan pou konfigire MetaAPI ou
          </span>
          <ChevronDown className={`h-4 w-4 text-[#8fb4e6] transition-transform ${showGuide ? "rotate-180" : ""}`} />
        </button>
        {showGuide && (
          <div data-testid="metaapi-guide" className="px-4 pb-4 pt-1 space-y-4 border-t border-[#14233d] text-sm text-[#a9c4ec]">

            {/* ETAP A */}
            <div className="space-y-1.5">
              <div className="text-[13px] font-bold text-white">ETAP A — Kreye kont MetaAPI ou</div>
              <ol className="list-decimal list-inside space-y-1 text-[12.5px]">
                <li>Ale sou <a href="https://app.metaapi.cloud/sign-up" target="_blank" rel="noopener noreferrer" className="text-[#1C8CFF] underline">app.metaapi.cloud</a> epi kreye yon kont gratis (imèl + modpas).</li>
                <li>Konfime imèl ou epi konekte nan tablodbò MetaAPI a.</li>
              </ol>
            </div>

            {/* ETAP B */}
            <div className="space-y-1.5">
              <div className="text-[13px] font-bold text-white">ETAP B — Jenere token ou</div>
              <ol className="list-decimal list-inside space-y-1 text-[12.5px]">
                <li>Ale nan meni <b className="text-white">"API access tokens"</b> (oswa <a href="https://app.metaapi.cloud/token" target="_blank" rel="noopener noreferrer" className="text-[#1C8CFF] underline">app.metaapi.cloud/token</a>).</li>
                <li>Klike <b className="text-white">"Generate token"</b>, chwazi opsyon ki anba yo, epi kopye token long lan.</li>
              </ol>
              <div className="rounded-xl border border-[#00F593]/30 bg-[#00F593]/5 p-3 space-y-1.5 mt-1" data-testid="metaapi-guide-choices">
                <div className="text-[12.5px] font-semibold text-[#00F593]">⚙️ Ki opsyon pou chwazi lè w ap jenere token an (enpòtan)</div>
                <ul className="space-y-1 text-[12px]">
                  <li>• <b className="text-white">Access:</b> <b className="text-white">ACCÈS KONPLÈ (Full access)</b> — <b className="text-white">PA</b> limite token an ("Do NOT narrow down").</li>
                  <li>• <b className="text-white">Applications:</b> kite <b className="text-white">TOUT</b> ladan (MetaApi + Account management + Trading).</li>
                  <li>• <b className="text-white">Role:</b> <b className="text-white">Manager / Full access</b> — <b className="text-white">PA</b> "Reader / Read-only".</li>
                  <li>• <b className="text-white">Resources:</b> <b className="text-white">All accounts</b> (tout kont).</li>
                  <li>• <b className="text-white">Expiration:</b> <b className="text-white">No expiration</b> pou token an pa sispann.</li>
                </ul>
              </div>
            </div>

            {/* ETAP C */}
            <div className="space-y-1.5">
              <div className="text-[13px] font-bold text-white">ETAP C — Rechaje kont MetaAPI ou</div>
              <ol className="list-decimal list-inside space-y-1 text-[12.5px]">
                <li>Ale nan <b className="text-white">"Billing"</b> sou MetaAPI epi ajoute lajan. Minimòm ou ka rechaje se <b className="text-white">$10 ou $20</b>.</li>
                <li>San rechaj, MetaAPI p ap kite kont ou konekte pou trade reyèl.</li>
              </ol>
            </div>

            {/* ETAP D */}
            <div className="space-y-1.5">
              <div className="text-[13px] font-bold text-white">ETAP D — Kole token an nan app sa a</div>
              <ol className="list-decimal list-inside space-y-1 text-[12.5px]">
                <li>Retounen isit la, kole token an nan bwat <b className="text-white">"Token MetaAPI ou"</b> anwo epi klike <b className="text-white">"Anrejistre"</b>.</li>
                <li>App la ap verifye token an tousuit — w ap wè yon badj <b className="text-[#22e07b]">Verifye ✓</b> si li bon.</li>
              </ol>
            </div>

            {/* ETAP E */}
            <div className="space-y-1.5">
              <div className="text-[13px] font-bold text-white">ETAP E — Konekte kont MT5 ou (de A a Z)</div>
              <ol className="list-decimal list-inside space-y-1 text-[12.5px]">
                <li>Ouvri paj <b className="text-white">MetaTrader</b> nan meni an epi klike <b className="text-white">"Konekte / Add Account"</b>.</li>
                <li><b className="text-white">Platfòm:</b> chwazi <b className="text-white">MT5</b> (MetaTrader 5).</li>
                <li>Antre <b className="text-white">Login</b> (nimewo kont ou), <b className="text-white">Modpas</b> (Master/Investor), epi <b className="text-white">Sèvè broker</b> ou an (egzakteman jan li parèt nan MT5 ou).</li>
                <li><b className="text-white">Rejyon (Region):</b> chwazi rejyon ki pi pre broker ou — pa egzanp <b className="text-white">New York</b> (pou broker US/Amerik) oswa <b className="text-white">London</b> (pou Ewòp). Si w pa sèten, kite l sou default/otomatik.</li>
                <li>Klike <b className="text-white">"Konekte / Fini"</b> epi tann kèk segond pou kont lan pwovizyone epi vin <b className="text-[#22e07b]">Connected</b>.</li>
              </ol>
            </div>

            {/* ETAP F */}
            <div className="space-y-1.5">
              <div className="text-[13px] font-bold text-white">ETAP F — Teste epi kòmanse</div>
              <ol className="list-decimal list-inside space-y-1 text-[12.5px]">
                <li>Retounen nan seksyon sa a epi klike <b className="text-white">"Teste"</b> pou konfime token ou toujou valab.</li>
                <li>Lè MT5 ou <b className="text-[#22e07b]">Connected</b>, ale nan paj prensipal la epi klike <b className="text-white">START</b> pou bòt la kòmanse trade anba pwòp kle pa w. ✅</li>
              </ol>
            </div>

            <div className="text-[11px] text-[#5f7ba6] border-t border-[#14233d] pt-2">
              Nòt: si w pa mete okenn token, app la ap kontinye itilize kle default platfòm nan — men ou ka gen limit.
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

/* ------------------------------- Page ------------------------------- */
export default function Settings() {
  const { user, bots, refreshAll } = useOutletContext();
  const { logout, refreshUser, playTransition, endTransition } = useAuth();
  const { accent, setAccent } = useTheme();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [openId, setOpenId] = useState("profile");
  const [risk, setRisk] = useState([bots[0]?.risk_percent || 1]);
  const [notif, setNotif] = useState(true);
  const [notifBusy, setNotifBusy] = useState(false);
  const [asstUnread, setAsstUnread] = useState(false);
  const license = user?.license;
  const noUsername = !user?.username;

  useEffect(() => {
    const checkUnread = async () => {
      const sid = localStorage.getItem("sfx_asst_session");
      if (!sid) return;
      try {
        const { data } = await api.get(`/assistant/unread?session_id=${sid}`);
        setAsstUnread(!!data.unread);
      } catch (e) { /* ignore */ }
    };
    checkUnread();
    const t = setInterval(checkUnread, 15000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => {
    await playTransition("logout");
    await logout();
    navigate("/login");
    setTimeout(endTransition, 700);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.post("/auth/profile", { name, username });
      await refreshUser();
      toast.success("Pwofil ou mete ajou");
      setOpenId(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const enableNotifs = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Navigatè ou pa sipòte notifikasyon push.");
      return;
    }
    setNotifBusy(true);
    try {
      await promptNotifications();
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      if (perm === "granted") {
        setNotif(true);
        toast.success("Notifikasyon aktive! W ap resevwa alèt signal, trade ak abònman.");
      } else if (perm === "denied") {
        toast.error("Notifikasyon bloke nan navigatè a. Pèmèt yo nan paramèt sit la epi eseye ankò.");
      } else {
        toast.info("Tanpri apwouve demann notifikasyon an nan navigatè a.");
      }
    } catch (e) {
      toast.error("Nou pa t ka aktive notifikasyon yo. Eseye ankò.");
    } finally {
      setNotifBusy(false);
    }
  };

  return (
    <div className="px-5 pt-6 pb-6 fade-up space-y-3">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-[#888] text-sm mt-1 mb-3">Manage your profile, risk and preferences.</p>
      </div>

      <Section icon={User} title="Profile" id="profile" openId={openId} onToggle={setOpenId}>
        {noUsername && (
          <div data-testid="username-prompt" className="flex items-center gap-2 text-xs text-[#f5a623] bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-xl px-3 py-2">
            <ArrowRight className="h-3.5 w-3.5" /> Tanpri ajoute yon username pou konplete pwofil ou.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-[#888]">Name</Label>
            <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl focus-visible:ring-0" />
          </div>
          <div>
            <Label className="text-xs text-[#888]">Username</Label>
            <div className="mt-1 flex items-center bg-[#060c18] border border-[#14233d] rounded-xl h-10 focus-within:border-[#1C8CFF]/50 transition-colors">
              <span className="pl-3 pr-1 text-[#888] select-none font-medium">@</span>
              <input data-testid="profile-username-input" value={username}
                onChange={(e) => setUsername(e.target.value.replace(/^@+/, ""))}
                placeholder="username"
                className="flex-1 bg-transparent pr-3 h-full outline-none text-white text-sm placeholder:text-[#555]" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs text-[#888]">Email</Label>
            <Input readOnly value={user?.email || ""} className="mt-1 bg-[#04070d] border-[#14233d] rounded-xl text-[#7f9bc4]" />
          </div>
        </div>
        <Button data-testid="save-profile-button" onClick={saveProfile} disabled={savingProfile}
          className="h-11 rounded-full text-white font-semibold w-full hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
          {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Profile"}
        </Button>
      </Section>

      <Section icon={ImagePlus} title="Bot Profile Picture" id="avatar" openId={openId} onToggle={setOpenId}>
        {(user?.license?.is_staff || user?.license?.tier === "elite") ? (
          <BotAvatarUpload bot={bots?.[0]} onUpdated={refreshAll} />
        ) : (
          <div data-testid="avatar-locked" className="border border-[#14233d] bg-[#060c18] rounded-2xl p-5 text-center">
            <div className="h-12 w-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
              <Lock className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} />
            </div>
            <p className="text-sm text-white font-semibold">Elite Feature</p>
            <p className="text-xs text-[#5f7ba6] mt-1">
              Sèlman manm plan <b className="text-[#8fb4e6]">Elite</b> ka chanje foto pwofil la. Amelyore plan w pou pèsonalize robo w la.
            </p>
          </div>
        )}
      </Section>

      <Section icon={Bell} title="Notifications" id="notifications" openId={openId} onToggle={setOpenId}>
        <p className="text-xs text-[#5f7ba6] -mt-1">Resevwa alèt sou signal, trade, ak abònman ou dirèkteman sou telefòn ou.</p>
        <Button data-testid="enable-notifications-button" onClick={enableNotifs} disabled={notifBusy}
          className="h-11 rounded-full text-white font-semibold w-full hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
          {notifBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Bell className="h-4 w-4 mr-1.5" /> Enable Push Notifications</>}
        </Button>
        <div className="flex items-center justify-between pt-3 border-t border-[#14233d] mt-3">
          <div><Label className="text-sm">Trade alerts</Label><p className="text-xs text-[#666]">Get notified when Smart Scalper opens or closes trades.</p></div>
          <Switch checked={notif} onCheckedChange={setNotif} data-testid="notif-switch" />
        </div>
      </Section>

      <Section icon={CandlestickChart} title="Binary Signal" id="binary" openId={openId} onToggle={setOpenId}>
        <p className="text-xs text-[#5f7ba6] -mt-1">Signal Engine — jenere siyal BUY/SELL ak analiz teknik konplè.</p>
        <Button data-testid="open-binary-signal-button" onClick={() => navigate("/binary-signal")}
          className="w-full h-11 rounded-2xl text-white font-semibold hover:opacity-90"
          style={{ backgroundColor: "var(--sfx-accent)" }}>
          <CandlestickChart className="mr-2 h-4 w-4" /> Ouvri Binary Signal
        </Button>
        <Button data-testid="open-pocket-bot-button" onClick={() => navigate("/pocket-bot")}
          className="w-full h-11 rounded-2xl font-semibold text-white hover:opacity-90"
          style={{ background: "linear-gradient(135deg,#8b5cf6,#c026d3)" }}>
          <CandlestickChart className="mr-2 h-4 w-4" /> Ouvri Pocket Bot (Auto · Simulator)
        </Button>
      </Section>

      <Section icon={Sparkles} title="Assistant" id="assistant" openId={openId} onToggle={setOpenId}
        action={asstUnread && (
          <span data-testid="assistant-unread-dot" className="flex items-center gap-1 text-[10px] font-semibold text-[#ff5b5b]">
            <span className="h-2 w-2 rounded-full bg-[#ff3b30] animate-pulse" /> Nouvo repons
          </span>
        )}>
        <p className="text-xs text-[#5f7ba6] -mt-1">Poze kesyon sou mache a, volatilite, plan trading, oswa kijan app la mache.</p>
        <AssistantDialog onRead={() => setAsstUnread(false)} />
      </Section>

      <MetaApiSection openId={openId} onToggle={setOpenId} />

      <Section icon={Gift} title="Refere & Touche" id="referral" openId={openId} onToggle={setOpenId}>
        <ReferralCard />
      </Section>

      <SupportSection openId={openId} onToggle={setOpenId} />

      <Section icon={ShieldCheck} title="Subscription" id="subscription" openId={openId} onToggle={setOpenId}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-semibold">{license?.active ? license.plan_name : "No active plan"}</div>
            <div className="font-mono-term text-xs uppercase tracking-widest mt-1 text-[#888]" data-testid="settings-license-status">
              {license?.active ? `Active · ${license.days_left} days left` : license?.status === "expired" ? "Expired" : "Inactive"}
            </div>
          </div>
          <Link to="/app/billing"><Button className="h-9 rounded-full px-4 text-white font-semibold hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>Manage <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
        </div>
      </Section>

      <Section icon={Palette} title="Appearance" id="appearance" openId={openId} onToggle={setOpenId}>
        <p className="text-xs text-[#5f7ba6] -mt-1">Pick an accent color to personalize your dashboard.</p>
        <div className="grid grid-cols-5 gap-3">
          {ACCENT_COLORS.map((c) => {
            const active = accent.toLowerCase() === c.hex.toLowerCase();
            return (
              <button key={c.hex} onClick={() => setAccent(c.hex)} data-testid={`color-${c.name.toLowerCase()}`}
                title={c.name}
                className="relative h-12 rounded-2xl transition-transform duration-200 hover:scale-105 flex items-center justify-center"
                style={{ backgroundColor: c.hex, boxShadow: active ? `0 0 18px ${c.hex}` : "none" }}>
                {active && <Check className="h-5 w-5 text-white" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      </Section>

      {["admin", "manager", "mentor", "partner"].includes(user?.role) && (
        <Section icon={ShieldCheck} title="Administrator" id="administrator" openId={openId} onToggle={setOpenId}>
          <p className="text-xs text-[#5f7ba6] -mt-1">Aksè rapid nan panèl jesyon yo.</p>
          {["admin", "manager", "mentor"].includes(user?.role) && (
            <Button onClick={() => navigate("/admin")} data-testid="admin-panel-button"
              className="w-full h-11 rounded-2xl font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: "var(--sfx-accent)" }}>
              <ShieldCheck className="mr-2 h-4 w-4" /> Ouvri Panèl Admin
            </Button>
          )}
          {user?.role === "partner" && (
            <Button onClick={() => navigate("/partner")} data-testid="partner-panel-button" variant="outline"
              className="w-full h-11 rounded-2xl border-[#1C8CFF]/50 bg-transparent text-white hover:bg-[#0f1a2e]">
              <Users className="mr-2 h-4 w-4 text-[#1C8CFF]" /> Ouvri Panèl Patnè
            </Button>
          )}
          {["admin", "manager"].includes(user?.role) && (
            <p className="text-[11px] text-[#5f7ba6] leading-relaxed">Jere patnè yo nan Panèl Admin → tab "Partners".</p>
          )}
        </Section>
      )}

      <Section icon={User} title="Account" id="account" openId={openId} onToggle={setOpenId}>
        <Button onClick={doLogout} data-testid="logout-button"
          className="w-full h-11 rounded-full bg-[#ff3b30]/15 text-[#ff5b5b] hover:bg-[#ff3b30]/25 font-semibold">
          <LogOut className="mr-2 h-4 w-4" /> Log Out
        </Button>
      </Section>
    </div>
  );
}
